import type { FAQType } from '@form-factory/schema';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Switch } from '@utima/ui';
import { useMemo } from 'react';

import { ActionsCell } from '@/components/dataTable/cells/ActionsCell';

import { useFAQMutation } from './useFAQMutation';

const columnHelper = createColumnHelper<FAQType>();

export function useFAQsCols() {
  const { t } = useLingui();
  const {
    remove: { mutateAsync: remove },
    update: { mutateAsync: update },
  } = useFAQMutation();

  return useMemo(
    () => [
      columnHelper.accessor(row => row.title, {
        id: 'title',
        header: t`Name`,
        meta: {
          sortable: 'title',
        },
      }),
      columnHelper.accessor(row => row.parent?.title, {
        id: 'parent.title',
        header: t`Parent`,
        meta: {
          sortable: 'parent.title',
        },
      }),
      columnHelper.accessor(row => row.question, {
        id: 'question',
        header: t`Question`,
        meta: {
          sortable: 'question',
        },
      }),
      columnHelper.accessor(row => row.answer, {
        id: 'answer',
        header: t`Answer`,
        meta: {
          sortable: 'answer',
        },
      }),
      columnHelper.accessor(row => row.clickCount, {
        id: 'clickCount',
        header: t`Click count`,
        meta: {
          sortable: 'clickCount',
        },
      }),
      columnHelper.accessor(row => row.updatedAt, {
        id: 'updatedAt',
        header: t`Updated`,
        cell: row => formatUtils.date(row.getValue(), DateFormat.DateTime),
        meta: {
          sortable: 'updatedAt',
        },
      }),
      columnHelper.accessor(row => row.active, {
        id: 'active',
        header: t`Active`,
        cell: row => (
          <Switch
            data-table='prevent'
            onClick={() =>
              update({ id: row.row.original.id, active: !row.getValue() })
            }
            checked={row.getValue()}
          />
        ),
        meta: {
          sortable: 'active',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => (
          <ActionsCell onDelete={() => remove({ id: row.original.id })} />
        ),
        meta: {
          align: 'center',
        },
      }),
    ],
    [remove, update, t],
  );
}
